import React from 'react';
import {
  EllipsisHorizontalIcon,
  GlobeAltIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import type { SavedView } from '../types';
import { getInitials, getAvatarColor } from './avatar-utils';

export interface SavedViewRowProps {
  view: SavedView;
  showAuthor: boolean;
  isActive: boolean;
  isDefault: boolean;
  isOwnView: boolean;
  isMenuOpen: boolean;
  onSelect: (view: SavedView) => void;
  onOpenMenu: (view: SavedView, rect: DOMRect) => void;
  onTooltipEnter: (e: React.MouseEvent<Element> | React.FocusEvent<Element>) => void;
  onTooltipLeave: () => void;
}

export const SavedViewRow: React.FC<SavedViewRowProps> = React.memo(
  ({
    view,
    showAuthor,
    isActive,
    isDefault,
    isOwnView,
    isMenuOpen,
    onSelect,
    onOpenMenu,
    onTooltipEnter,
    onTooltipLeave,
  }) => {
    const ownerBadge = isOwnView && view.is_public && !view.is_system;
    const authorBadge = showAuthor && view.author_email && view.author_name !== 'System';
    // Accessible label surfaces the same information tooltips expose visually.
    const ariaLabel = [
      view.name,
      view.description,
      ownerBadge && 'Public',
      authorBadge && view.author_name ? `by ${view.author_name}` : null,
      isDefault && 'Default',
    ]
      .filter(Boolean)
      .join(', ');

    const handleClick = () => onSelect(view);
    const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the outer row click from firing after the menu trigger.
      e.stopPropagation();
      onOpenMenu(view, e.currentTarget.getBoundingClientRect());
    };

    return (
      <div
        role="menuitem"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-current={isActive ? 'true' : undefined}
        className={clsx(
          'group relative flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md transition-colors text-sm',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/40',
          isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
        )}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <span className="flex-1 min-w-0 flex items-center gap-1.5">
          <span className="truncate">{view.name}</span>
          {view.description && (
            <InformationCircleIcon
              aria-hidden="true"
              data-tooltip={view.description}
              className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
              onMouseEnter={onTooltipEnter}
              onMouseLeave={onTooltipLeave}
            />
          )}
        </span>
        {ownerBadge && (
          <GlobeAltIcon
            aria-hidden="true"
            data-tooltip="Public — visible to team"
            className="h-3.5 w-3.5 text-gray-400 flex-shrink-0"
            onMouseEnter={onTooltipEnter}
            onMouseLeave={onTooltipLeave}
          />
        )}
        {authorBadge && (
          <span
            aria-hidden="true"
            data-tooltip={
              view.author_name
                ? `${view.author_name}\n${view.author_email}`
                : (view.author_email as string)
            }
            className={clsx(
              'flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold text-white flex-shrink-0 cursor-default',
              getAvatarColor(view.author_email as string)
            )}
            onMouseEnter={onTooltipEnter}
            onMouseLeave={onTooltipLeave}
          >
            {getInitials(view.author_name ?? (view.author_email as string))}
          </span>
        )}
        {isDefault && (
          <StarIconSolid
            aria-hidden="true"
            className="h-3.5 w-3.5 text-amber-500 flex-shrink-0"
          />
        )}
        {/* Actions menu trigger — always visible */}
        <button
          type="button"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          className="p-0.5 rounded hover:bg-gray-200 flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          onClick={handleMenuClick}
        >
          <EllipsisHorizontalIcon aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    );
  }
);
SavedViewRow.displayName = 'SavedViewRow';
